import React, { useEffect, useState } from "react";
import { api, supabase } from "../api";
import type { definitions } from "../../types/supabase";
import Cookies from "js-cookie";
import { useNavigate, useSearchParams } from "react-router-dom";
import uuid from "react-native-uuid";

import { brandConfig } from "../helpers/brandConfig";

import Button from "../components/buttons/Button";
import PasswordField from "../components/PasswordField";
import Snackbar from "../components/snackbar/snackbar";

export default function Enroll() {
  const [searchParams, _setSearchParams] = useSearchParams();
  const email = decodeURIComponent(searchParams.get("email") || "");
  const name = decodeURIComponent(searchParams.get("name") || "");
  const offerIds = decodeURIComponent(searchParams.get("offerIds") || "");
  const passwordRequired =
    decodeURIComponent(searchParams.get("passwordRequired") || "false") ===
    "true";
  const clientSideId = decodeURIComponent(
    searchParams.get("clientSideId") || ""
  );
  const transactionURL = decodeURIComponent(
    searchParams.get("transactionURL") || ""
  );

  const [userEmail, setUserEmail] = useState(email);
  const [userPassword, setUserPassword] = useState("");
  const [userConfirmPassword, setUserConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedOffer, setSelectedOffer] = useState<
    definitions["offers"] | string | null
  >(null);

  const navigate = useNavigate();
  const cookies = Cookies.get();
  const userAgent = navigator.userAgent;

  // Helper function to extract offer ID from either an offer object or string ID
  const getOfferId = (
    offer: definitions["offers"] | string | null
  ): string | null => {
    if (!offer) return null;
    return typeof offer === "string" ? offer : offer.id;
  };

  // Helper function to extract offer name from an offer object
  const getOfferName = (
    offer: definitions["offers"] | string | null
  ): string => {
    if (!offer) return "course";
    return typeof offer === "string" ? "course" : offer.name;
  };

  useEffect(() => {
    (async () => {
      const foundSelectedOffer = await api.getDefaultOfferOrOffer(offerIds);
      setSelectedOffer(foundSelectedOffer);
      let { existingUserId } = await api.isUserRegistered(email);
      if (passwordRequired === false) {
        let password = uuid.v4().toString();
        if (!existingUserId) {
          const { data } = await supabase.auth.signUp({
            email: email,
            password: password,
          });

          existingUserId = data.user?.id;
        }

        const offerId = getOfferId(foundSelectedOffer);
        if (!offerId) {
          console.error("No valid offer ID found");
          navigate("/login-form");
          return;
        }

        const response = await api.registerForIntroductoryOffers(
          clientSideId.toString(),
          cookies,
          email.toLowerCase(),
          password.toString(),
          transactionURL,
          userAgent,
          name,
          offerId,
          existingUserId
        );

        if (response.ok) {
          const json = await response.json();
          if (json.redirectUrl) {
            window.location = json.redirectUrl;
          } else {
            navigate("/login-form");
          }
        } else {
          navigate("/login-form");
        }
      } else {
        if (existingUserId) {
          setErrorMessage("You already have an account!");
          navigate("/login-form");
        }
      }
    })();
  }, []);

  const createUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (userPassword !== userConfirmPassword) {
      setErrorMessage("Passwords must match");
      return;
    }

    let foundSelectedOffer = selectedOffer;
    if (!foundSelectedOffer) {
      foundSelectedOffer = await api.getDefaultOfferOrOffer(offerIds);
    }

    const offerId = getOfferId(foundSelectedOffer);
    if (!offerId) {
      setErrorMessage("Unable to find a valid offer. Please try again.");
      return;
    }

    let { existingUserId } = await api.isUserRegistered(
      userEmail.toLowerCase()
    );
    if (!existingUserId) {
      const { data } = await supabase.auth.signUp({
        email: userEmail.toLowerCase(),
        password: userPassword,
      });

      existingUserId = data.user?.id;
    }

    const response = await api.registerForIntroductoryOffers(
      clientSideId.toString(),
      cookies,
      userEmail.toLowerCase(),
      userPassword,
      transactionURL,
      userAgent,
      name,
      offerId,
      existingUserId
    );

    if (response.ok) {
      const json = await response.json();
      if (json.redirectUrl) {
        window.location = json.redirectUrl;
      } else {
        navigate("/login-form");
      }
    } else {
      navigate("/login-form");
    }
  };

  if (passwordRequired === false) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-xl p-12 pb-4 w-3/4 md:w-1/2 lg:w-2/5 2xl:w-1/4 m-auto">
      <Snackbar
        message={errorMessage}
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage("")}
      />

      <a
        href={brandConfig.brandHomepageLink()}
        target="_blank"
        rel="noreferrer"
      >
        <img
          className="flex flex-col w-3/4 pb-6 m-auto cursor-pointer"
          src={brandConfig.navbarImage()}
          alt="brand logo"
        />
      </a>
      <form className="flex flex-col" onSubmit={createUser}>
        <input
          id="email"
          type="email"
          className={`p-2 mt-2 w-full`}
          placeholder="Email"
          data-testid="enroll-email-input"
          onChange={(e) => setUserEmail(e.target.value)}
          value={userEmail}
        />
        <PasswordField
          handleInfoChange={(e: any) => setUserPassword(e.target.value)}
          placeholder={"Create Password"}
          value={userPassword}
          data-testid="password-input"
        />
        <PasswordField
          handleInfoChange={(e: any) => setUserConfirmPassword(e.target.value)}
          placeholder="Confirm Password"
          value={userConfirmPassword}
          data-testid="password-confirmation-input"
        />
        <p className="text-sm text-gray-600 mt-3 mb-1 text-center">
          Set a quick password to access your &quot;
          {getOfferName(selectedOffer)}&quot; lessons anytime and pick up where
          you left off
        </p>
        <Button
          additionalStyling="p-2 mt-4"
          data-testid="set-password"
          style="primary"
          type="submit"
          buttonId="set-password-button"
        >
          {"Create your password"}
        </Button>
      </form>
    </div>
  );
}
